import type { PokerLevelFragment } from '../__generated__/hooks'

/**
 * Big-blind ratio. Null when bigBlind is missing or zero — typically pre-
 * first-level (Seated state). Rounded to nearest integer; poker players
 * think in whole BBs, not decimals.
 */
export const bbRatio = (
  stack: number | null | undefined,
  bigBlind: number | null | undefined
): number | null => {
  if (!stack || !bigBlind || bigBlind <= 0) return null
  return Math.round(stack / bigBlind)
}

/**
 * Pay-jump intel for the SeatCard.
 *
 * Three flavours:
 *  - `bubble`: pre-ITM, `n` more players need to bust before money. Drives
 *    the BubbleBanner copy ("Boblen: 2 fra premie").
 *  - `secured`: ITM. The minimum a player is now guaranteed (`positionIfNow`
 *    is their position if the tournament ended right this second).
 *  - `nextJump`: ITM with another rung above. The kr delta to climb one
 *    spot — the most-cited reason late-stage tournament players fold.
 *
 * `null` when payouts disabled, prize pool missing, or final table won.
 */
export type PayJump =
  | { kind: 'bubble'; toGo: number }
  | {
      kind: 'secured'
      positionIfNow: number
      securedKr: number
      nextPositionKr: number | null
    }

export const payJumpFor = ({
  playersLeft,
  payouts,
  prizePool,
  usePayouts,
}: {
  playersLeft: number | null | undefined
  payouts: ReadonlyArray<{ position: number; percentageBp: number }>
  prizePool: number | null | undefined
  usePayouts: boolean
}): PayJump | null => {
  if (!usePayouts || !payouts.length || !playersLeft || !prizePool) return null

  const payoutCount = payouts.length
  if (playersLeft > payoutCount) {
    return { kind: 'bubble', toGo: playersLeft - payoutCount }
  }

  // Player's implied finish if tournament ended now == playersLeft (since
  // they're still in, in last-eliminated order). Find the kr at that
  // position + the next rung up.
  const positionIfNow = playersLeft
  const krMap = nokAmounts(payouts, prizePool)
  const securedKr = krMap.get(positionIfNow) ?? 0

  // Next rung = better finishing position (smaller number). Final-table
  // winner has no next rung — return null for that slot.
  const nextPosition = positionIfNow - 1
  const nextPositionKr =
    nextPosition >= 1 ? krMap.get(nextPosition) ?? null : null
  return { kind: 'secured', positionIfNow, securedKr, nextPositionKr }
}

/**
 * Round-down-100 + residual-to-1st, matching `PayoutEditor` and
 * `util/payouts.ts`. Duplicated here to keep this file dependency-free
 * (and import-cycle-free) — both use the same formula.
 */
const nokAmounts = (
  payouts: ReadonlyArray<{ position: number; percentageBp: number }>,
  prizePool: number
): Map<number, number> => {
  const sorted = [...payouts].sort((a, b) => a.position - b.position)
  const rounded = sorted.map(
    (p) => Math.floor(((p.percentageBp / 10000) * prizePool) / 100) * 100
  )
  const residual = prizePool - rounded.reduce((acc, n) => acc + n, 0)
  const out = new Map<number, number>()
  sorted.forEach((p, i) => {
    out.set(p.position, i === 0 ? rounded[i] + residual : rounded[i])
  })
  return out
}

/**
 * Minutes until the first upcoming break, counted from the current
 * level's countdown. `null` when currently in a break, when the current
 * level has no break after it, or when the clock isn't running.
 *
 * With breaks-as-property, a break is attached to a level via
 * `breakAfterMinutes`. If the current level has one → it kicks in after
 * this level's remaining time. Deeper look-ahead (e.g. "next break in
 * 42 min" spanning multiple levels) would need every level, not just
 * current + next; skipped for now.
 */
export const minutesToBreak = ({
  currentLevel,
  inBreak,
  remainingSec,
}: {
  currentLevel: PokerLevelFragment | null | undefined
  inBreak: boolean
  remainingSec: number
}): number | null => {
  if (!currentLevel) return null
  if (inBreak) return null
  if ((currentLevel.breakAfterMinutes ?? 0) > 0) {
    return Math.max(0, Math.ceil(remainingSec / 60))
  }
  return null
}
